/* eslint-disable @typescript-eslint/no-explicit-any */
import * as z from 'zod';

import { ApolloError, makeVar } from '@apollo/client';
import { getItem, removeItem, setItem } from '../utils/session';

import { ActivityType } from '../../types/__generated__';
import { EventVariant } from '../constants/EventVariant';
import { ReactiveVar } from '@apollo/client/cache/inmemory/reactiveVars';
import { Result } from '../utils/result';
import { captureMessage } from '@sentry/react';
import { parseJson } from '../utils/json';

const fundraisingDefaults = z.object({
  coverImage: z.string().nullish(),
  story: z.string().optional(),
  targetAmount: z
    .object({
      value: z.number(),
      currencyCode: z.string(),
    })
    .optional(),
  title: z.string().optional(),
});

const causeSchema = z.object({
  id: z.string(),
  beneficiaryId: z.string(),
  externalId: z.string(),
  landingPageId: z.string(),
  name: z.string(),
  logo: z.string().optional(),
  campaignId: z.string().optional(),
  campaignTitle: z.string().optional(),
  campaignLogo: z.string().optional(),
  supportsGiftAid: z.boolean(),
  registrationNumber: z.string().optional(),
  fundraisersCount: z.number().optional(),
  countryCode: z.string().optional(),
  startFundraisingUrl: z.string().optional(),
  defaults: fundraisingDefaults,
  fundraisingPageUrl: z.string().optional(),
});

const occasionSchema = z.object({
  type: z
    .enum(['BIRTHDAY', 'WEDDING', 'IN_MEMORY', 'OTHER'])
    .nullable()
    .optional(),
  date: z.string().nullable().optional(),
  names: z.array(z.string()),
  description: z.string().nullable().optional(),
});

const activitySchema = z.object({
  type: z.nativeEnum(ActivityType).nullable().optional(),
  date: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
});

const mediaTypeSchema = z.enum(['PHOTO', 'VIDEO', 'LIVESTREAM']);

const pageCoverSchema = z.object({
  mediaType: mediaTypeSchema,
  mediaUrl: z.string().optional(),
});

const consentSchema = z.object({
  id: z.number(),
  type: z.string(),
  text: z.string(),
  entityId: z.string(),
  consentGiven: z.boolean(),
});

const eventSourceSchema = z
  .enum(['fundraising', 'campaign', 'charity', 'team'])
  .optional();

const eventSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  totalFundraisers: z.number().optional(),
  date: z.string().optional(),
  externalId: z.string().optional(),
  isDeepLink: z.boolean().optional(),
  activityType: z.nativeEnum(ActivityType).optional(),
  eventSource: eventSourceSchema,
  defaults: fundraisingDefaults.optional(),
  startFundraisingUrl: z.string().url().or(z.literal('')).nullish(),
});

const storySourceSchema = z
  .enum(['fundraising', 'campaign', 'charity', 'team'])
  .optional();

const teamSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const ownerSchema = z.object({
  id: z.string(),
});

export const category = z.enum([
  'animals',
  'childrenAndYouth',
  'community',
  'creative',
  'disabilitySupport',
  'education',
  'emergencies',
  'environment',
  'funeralsAndMemorials',
  'lgbtq',
  'medical',
  'politics',
  'socialWelfare',
  'sports',
  'volunteering',
  'other',
  'funeral',
]);

const pageSchema = z.object({
  cause: causeSchema.optional(),
  event: eventSchema.optional(),
  targetAmount: z.number().optional(),
  targetCurrency: z.enum(['GBP', 'EUR', 'USD', 'AUD', 'NZD', 'CAD']).optional(),
  occasion: occasionSchema.optional().nullable(),
  activity: activitySchema.optional().nullable(),
  shortName: z.string().optional(),
  title: z.string().optional(),
  story: z.string().optional(),
  url: z.string().optional(),
  storySource: storySourceSchema,
  sourceId: z.string().optional(),
  id: z.string().optional(),
  cover: pageCoverSchema.optional(),
  consents: z.array(consentSchema).optional(),
  team: teamSchema.optional(),
  owner: ownerSchema.optional(),
  coverMediaCollectionId: z.string().optional(),
  galleryMediaCollectionId: z.string().optional(),
  category: z
    .object({
      type: category,
      customCategoryName: z.string().optional(),
    })
    .optional(),
});

export const appStateSchema = z
  .object({
    page: pageSchema,
    userComesFromLegacyFlow: z.boolean().optional(),
    pcid: z.string().optional(),
    skipActivitySelection: z.boolean().optional(),
    eventVariant: z.nativeEnum(EventVariant).optional(),
    pageCreatedUrl: z.string().optional(),
    utmSource: z.string().optional(),
    utmCampaign: z.string().optional(),
    charityRegionAlertDismissed: z.boolean().optional(),
    isDeepLink: z.boolean().optional(),
    environmentId: z.string().optional(),
    isStartFundraisingSummaryVisited: z.boolean().optional(),
  })
  .strict();

export type Cause = z.infer<typeof causeSchema>;
export type Event = z.infer<typeof eventSchema>;
export type Page = z.infer<typeof pageSchema>;
export type PageCover = z.infer<typeof pageCoverSchema>;
export type AppState = z.infer<typeof appStateSchema>;
export type OccasionType = 'BIRTHDAY' | 'WEDDING' | 'IN_MEMORY' | 'OTHER';
export type Currency = Page['targetCurrency'];
export const MediaTypes = mediaTypeSchema.Enum;
export type MediaType = z.infer<typeof mediaTypeSchema>;
export type Consent = z.infer<typeof consentSchema>;
export type Occasion = z.infer<typeof occasionSchema>;
export type Activity = z.infer<typeof activitySchema>;
export type StorySource = z.infer<typeof storySourceSchema>;
export type Cover = z.infer<typeof pageCoverSchema>;
export type Team = z.infer<typeof teamSchema>;
export type Category = z.infer<typeof category>;

export const pageVar = makeVar<Page | undefined>(undefined);
export const userComesFromLegacyFlowVar = makeVar<boolean>(false);
export const pcidVar = makeVar<string | undefined>(undefined);
export const eventVariantVar = makeVar<EventVariant | undefined>(undefined);
export const pageCreatedUrlVar = makeVar<string | undefined>(undefined);
export const utmSourceVar = makeVar<string | undefined>(undefined);
export const utmCampaignVar = makeVar<string | undefined>(undefined);
export const skipActivitySelectionVar = makeVar<boolean | undefined>(undefined);
export const selectedInMemoryVar = makeVar<string | undefined>(undefined);
export const charityRegionAlertDismissedVar = makeVar<boolean>(false);
export const isDeepLinkVar = makeVar<boolean>(false);
export const environmentIdVar = makeVar<string | undefined>(undefined);
export const isStartFundraisingSummaryVisitedVar = makeVar<boolean>(false);

export const stateVars = {
  pageVar,
  pcidVar,
  skipActivitySelectionVar,
  userComesFromLegacyFlowVar,
  eventVariantVar,
  pageCreatedUrlVar,
  utmSourceVar,
  utmCampaignVar,
  charityRegionAlertDismissedVar,
  isDeepLinkVar,
  environmentIdVar,
  isStartFundraisingSummaryVisitedVar,
};

export const apolloErrorVar = makeVar<ApolloError | undefined>(undefined);
export const apolloLoadingVar = makeVar<boolean>(false);

export function restoreAppState(
  sessionId: string,
  stateVars: Record<string, ReactiveVar<any>>,
  appStateSchema: z.ZodObject<any, any>
): { fresh: boolean } {
  const data = getSession(sessionId);
  let fresh = true;

  if (data) {
    const result = parsePersistedData(data, appStateSchema);
    if (result.success) {
      for (const [key, rvar] of Object.entries(stateVars)) {
        rvar(result.data[key.substr(0, key.length - 3)]);
      }

      fresh = false;
    } else {
      const message = `The persisted application state for session id '${sessionId}' is invalid, a fresh state will be used instead. ${result.error.message}`;
      console.warn(message);
      captureMessage(message);
    }
  }

  return { fresh };
}

export function getSession(sessionId: string) {
  return getItem(`appState:${sessionId}`);
}

export function persistAppState<TAppState>(
  sessionId: string,
  appState: TAppState
) {
  setItem(`appState:${sessionId}`, JSON.stringify(appState));
}

export function removeAppState(sessionId: string) {
  removeItem(`appState:${sessionId}`);
}

function parsePersistedData(
  data: string,
  appStateSchema: z.ZodObject<any, any>
): Result {
  const jsonParseResult = parseJson(data);

  if (!jsonParseResult.success) {
    return jsonParseResult;
  }

  return appStateSchema.safeParse(jsonParseResult.data);
}

const unsubscribes = new Map<string, () => void>();

export function createSubscriptions(
  sessionId: string,
  stateVars: Record<string, ReactiveVar<any>>
) {
  for (const [key, rvar] of Object.entries(stateVars)) {
    const callback = () => {
      persistAppState(sessionId, getAppState(stateVars));

      unsubscribes.set(key, rvar.onNextChange(callback));
    };

    unsubscribes.get(key)?.();
    unsubscribes.set(key, rvar.onNextChange(callback));
  }
}

function getAppState(stateVars: Record<string, ReactiveVar<any>>): AppState {
  const appState: Record<string, any> = {};

  for (const [key, rvar] of Object.entries(stateVars)) {
    appState[key.substr(0, key.length - 3)] = rvar();
  }

  return appState as AppState;
}
